/**
 * Wires the registry (lib/actions.ts) to the app: the default way each verb runs (open a run,
 * stop it) and the `ActionCtx` a screen passes to `actions()`. A screen that owns a verb the
 * default cannot do (landing needs the inbox item's machine) passes its own handler; a verb with
 * neither is shown disabled with a reason, never dropped.
 */
import { useNavigate } from '@tanstack/react-router'
import { useMemo, useRef } from 'react'
import { useToast } from '../components/ui.js'
import { type ActionCtx, type ActionEntity, actions } from './actions.js'
import { useBackend } from './backend.js'
import { projectHref, runHref } from './ia.js'
import { landGrantOpt, useLandGrantPicker } from './land-grant.js'
import { runMachine } from './run-machine.js'
import { useSelectionActions } from './selection-actions.js'

export type VerbHandlers = Partial<Record<string, (e: ActionEntity) => void>>

const NAV_VERBS = new Set(['open', 'tail', 'logs'])

export function useActionCtx(o: { projectId?: string; handlers?: VerbHandlers } = {}): ActionCtx {
  const nav = useNavigate()
  const b = useBackend()
  const toast = useToast()
  const grantFor = useLandGrantPicker()
  const grantRef = useRef(grantFor)
  grantRef.current = grantFor
  const latest = useRef(o)
  latest.current = o
  return useMemo<ActionCtx>(() => {
    const runOf = (e: ActionEntity) =>
      e.kind === 'run' ? e.id : e.kind === 'task' ? e.runId : undefined
    const has = (verb: string, e: ActionEntity) =>
      !!latest.current.handlers?.[verb] ||
      (e.kind === 'run' && verb === 'stop') ||
      (e.kind === 'landing' && verb === 'land' && !!e.machineId && !!e.workstreamId) ||
      (e.kind === 'lane' && verb === 'build' && !!e.machineId) ||
      (NAV_VERBS.has(verb) && (!!runOf(e) || (!!latest.current.projectId && e.kind !== 'landing')))
    return {
      unavailable: (verb, e) =>
        has(verb, e) ? undefined : 'Not available from here yet. Open the run to do this.',
      do: (verb, e) => {
        const own = latest.current.handlers?.[verb]
        if (own) return own(e)
        const runId = runOf(e)
        if (NAV_VERBS.has(verb)) {
          const pid = latest.current.projectId
          if (runId) return void nav({ href: runHref(runId, pid) })
          if (pid) return void nav({ href: projectHref(pid, '', `${e.kind}:${e.id}`) })
        }
        if (verb === 'land' && e.kind === 'landing' && e.machineId && e.workstreamId) {
          const { machineId, workstreamId } = e
          void (async () => {
            try {
              await b.dispatch(
                'land.merge',
                machineId,
                {
                  workstreamId,
                  ...(e.runId ? { runId: e.runId } : {}),
                  ...(e.branch ? { branch: e.branch } : {}),
                  target: 'main',
                },
                {
                  summary: `Land ${e.branch || 'this branch'} into main`,
                  ...landGrantOpt(
                    grantRef.current({ workstreamId, projectId: e.projectId }, machineId),
                  ),
                },
              )
              toast('Landing started')
            } catch (err) {
              toast((err as Error).message || 'Could not start the landing.', 'error')
            }
          })()
          return
        }
        if (verb === 'build' && e.kind === 'lane' && e.machineId) {
          const { machineId, projectId, id } = e
          void (async () => {
            try {
              // Approves a lane waiting for a tap, or starts a manual build; production lanes
              // step up to a passkey (the daemon rejects a plain tap for them).
              await b.dispatch(
                'land.deploy',
                machineId,
                { projectId, lane: id },
                {
                  summary: `${e.production ? 'Release' : 'Build'} ${e.title ?? id}`,
                  ...(e.production ? { production: true } : {}),
                },
              )
              toast(e.production ? 'Release started' : 'Build started')
            } catch (err) {
              toast((err as Error).message || 'Could not start the build.', 'error')
            }
          })()
          return
        }
        if (verb === 'stop' && e.kind === 'run')
          void (async () => {
            try {
              const machine = await runMachine(undefined, e.id, () => b.api.runs())
              await b.dispatch('run.interrupt', machine, { runId: e.id, cancelQueued: true })
              toast('Stopped')
            } catch (err) {
              toast((err as Error).message || 'Could not stop the run.', 'error')
            }
          })()
      },
    }
  }, [nav, b, toast])
}

/** Compute the entity's actions, publish them as the current selection, and return them. */
export function useEntityActions(
  owner: string,
  entity: ActionEntity | undefined,
  o: { projectId?: string; handlers?: VerbHandlers } = {},
) {
  const ctx = useActionCtx(o)
  const list = useMemo(() => (entity ? actions(entity, ctx) : []), [entity, ctx])
  useSelectionActions(owner, entity?.title ?? '', entity ? list : [])
  return list
}
